import type { ModuleManifest } from "../../contracts/types";

/**
 * DEVELOPMENT FIXTURE / NON-AUTHORITATIVE
 * Ported from deckos-gamebook-proof ore. Does not establish Arcopolis canon.
 */
export const sector47ModuleProof: ModuleManifest = {
  id: "arcopolis-sector-47-illegal-route",
  title: "Sector 47 // The Illegal Route",
  version: "0.1.0-proof",
  classification: "DEVELOPMENT FIXTURE / NON-AUTHORITATIVE",
  startSceneId: "boot",
  startingState: {
    attributes: { body: 3, mind: 4, soul: 2 },
    resources: { integrity: 8, stress: 0, signal: 0 },
    inventory: ["polytool", "signal-ward"],
    flags: [],
    lore: [],
    discovered: ["boot", "civic-core"],
  },
  scenes: [
    {
      id: "boot",
      type: "PASSAGE",
      x: 50,
      y: 10,
      state: "DISCOVERED",
      title: "BOOT SEQUENCE",
      subtitle: "DeckOS field module",
      body: [
        "SECTOR 47 resolves through a damaged municipal index.",
        "A route exists beneath Civic Core that the official map denies.",
      ],
      actions: [
        {
          id: "begin",
          label: "ENTER CIVIC CORE",
          detail: "Initialize the field session",
          target: "civic-core",
          effects: [{ type: "discover", sceneId: "civic-core" }],
        },
      ],
    },
    {
      id: "civic-core",
      type: "HUB",
      x: 50,
      y: 29,
      state: "DISCOVERED",
      title: "CIVIC CORE // COUNTER NINE",
      subtitle: "Sector 47 public services",
      body: [
        "A cramped civic counter sits behind thick glass. Paper forms queue beneath failing lights.",
        "A service hatch is fitted beneath the eastern terminal. Its lower hinge carries fresh tool marks.",
      ],
      present: ["counter clerk", "maintenance runner", "waiting citizens"],
      objects: [
        "public terminal",
        "notice board",
        "queue kiosk",
        "service hatch",
      ],
      actions: [
        {
          id: "inspect-hatch",
          verb: "INSPECT",
          targets: ["hatch", "service hatch", "tool marks"],
          label: "INSPECT HATCH",
          detail: "Examine the hatch and tool marks",
          consequence:
            "Fresh scoring crosses the hinge. DeckOS resolves a lower route.",
          effects: [
            { type: "flag", value: "SERVICE_ROUTE_DISCOVERED" },
            { type: "discover", sceneId: "service-subgrid" },
            { type: "lore", value: "municipal-erasure" },
          ],
        },
        {
          id: "query-terminal",
          verb: "QUERY",
          targets: [
            "terminal",
            "public terminal",
            "records",
            "civic records",
            "civic records terminal",
          ],
          label: "QUERY CIVIC RECORDS TERMINAL",
          detail: "Open the municipal records terminal interface",
          consequence:
            "The public terminal hands you into a sealed records pane.",
          effects: [{ type: "discover", sceneId: "civic-records-terminal" }],
          target: "civic-records-terminal",
        },
        {
          id: "talk-runner",
          verb: "TALK",
          targets: ["runner", "maintenance runner"],
          label: "TALK TO RUNNER",
          detail: "Ask who opened the hatch",
          consequence:
            "The runner refuses the official channel, but whispers the name MORA.",
          effects: [
            { type: "flag", value: "MORA_NAMED" },
            { type: "discover", sceneId: "mora-channel" },
          ],
          target: "mora-channel",
        },
        {
          id: "move-subgrid",
          verb: "MOVE",
          targets: ["subgrid", "service subgrid", "hatch"],
          label: "ENTER SUBGRID",
          detail: "Cross the illegal threshold",
          requires: [{ type: "flag", value: "SERVICE_ROUTE_DISCOVERED" }],
          target: "service-subgrid",
        },
        {
          id: "report-now",
          label: "FILE AN OFFICIAL REPORT",
          detail: "End the anomaly under municipal authority",
          target: "official-record",
        },
      ],
    },
    {
      id: "mora-channel",
      type: "PASSAGE",
      x: 18,
      y: 47,
      state: "UNKNOWN",
      title: "MORA // BACKCHANNEL",
      subtitle: "Unregistered district contact",
      body: [
        "Mora answers through a burst channel: Do not open the route unless you are prepared to lose the official version of yourself.",
      ],
      actions: [
        {
          id: "trust-mora",
          label: "TRUST MORA",
          detail: "Accept the warning and request guidance",
          consequence:
            "Mora transmits a maintenance cipher and a single instruction: LISTEN BEFORE YOU JACK IN.",
          effects: [
            { type: "flag", value: "MORA_TRUST" },
            { type: "inventory", value: "maintenance-cipher" },
          ],
          target: "civic-core",
        },
        {
          id: "reject-mora",
          label: "CLOSE CHANNEL",
          detail: "Return without accepting help",
          target: "civic-core",
        },
      ],
    },
    {
      id: "service-subgrid",
      type: "ROOM",
      x: 50,
      y: 50,
      state: "UNKNOWN",
      title: "SERVICE SUBGRID",
      subtitle: "Threshold beneath the civic floor",
      body: [
        "Condensation clicks against old conduit. Chewed insulation marks the western branch.",
        "A violet pulse answers from a sealed access node to the east.",
      ],
      present: ["no confirmed contacts"],
      objects: ["loose cable", "rusted panel", "relic access seal"],
      actions: [
        {
          id: "listen-subgrid",
          verb: "LISTEN",
          targets: ["room", "subgrid", "seal", "movement"],
          label: "LISTEN",
          detail: "Read vibration through the conduit",
          consequence:
            "Two patterns separate: animal movement west; machine cadence east.",
          effects: [
            { type: "flag", value: "PRESSURES_IDENTIFIED" },
            { type: "discover", sceneId: "cable-pocket" },
            { type: "discover", sceneId: "relic-seam" },
          ],
        },
        {
          id: "take-cable",
          verb: "TAKE",
          targets: ["cable", "loose cable"],
          label: "TAKE LOOSE CABLE",
          detail: "Salvage a conductive length",
          consequence: "You coil the cable into your field kit.",
          effects: [{ type: "inventory", value: "conductive-cable" }],
        },
        {
          id: "move-cable",
          verb: "MOVE",
          targets: ["west", "cable pocket", "cable-rats"],
          label: "ENTER WESTERN POCKET",
          detail: "Follow the damaged insulation",
          requires: [{ type: "flag", value: "PRESSURES_IDENTIFIED" }],
          target: "cable-pocket",
        },
        {
          id: "move-relic",
          verb: "MOVE",
          targets: ["east", "relic seam", "seal"],
          label: "APPROACH RELIC SEAM",
          detail: "Follow the violet machine cadence",
          requires: [{ type: "flag", value: "PRESSURES_IDENTIFIED" }],
          target: "relic-seam",
        },
        {
          id: "return-core",
          verb: "RETURN",
          targets: ["core", "civic core", "hatch"],
          label: "RETURN TO CIVIC CORE",
          detail: "Climb back through the hatch into a changed floor",
          effects: [
            { type: "flag", value: "SUBGRID_TRAVERSED" },
            { type: "discover", sceneId: "returned-civic-core" },
          ],
          target: "returned-civic-core",
        },
      ],
    },
    {
      id: "civic-records-terminal",
      type: "TERMINAL",
      x: 72,
      y: 29,
      state: "DISCOVERED",
      title: "CIVIC RECORDS TERMINAL",
      subtitle: "Municipal index interface",
      body: [
        "The records pane is colder than the counter glass. Maintenance indices sit behind a thin authentication veil.",
        "One folder is marked SERVICE ROUTES // ARCHIVED. The revision stamp is newer than the demolition notice.",
      ],
      present: ["no staff presence"],
      objects: ["civic records terminal", "maintenance index", "revision stamp"],
      actions: [
        {
          id: "inspect-maintenance-records",
          verb: "INSPECT",
          targets: [
            "maintenance",
            "maintenance records",
            "records",
            "service routes",
            "folder",
          ],
          label: "INSPECT MAINTENANCE RECORDS",
          detail: "Read the archived service-route evidence",
          consequence:
            "Erasure marks sit under the demolition claim. DeckOS tags a municipal record anomaly.",
          effects: [
            { type: "flag", value: "MUNICIPAL_RECORD_ANOMALY" },
            { type: "flag", value: "ARCHIVE_CONFLICT" },
            { type: "lore", value: "erased-service-routes" },
          ],
        },
        {
          id: "query-records-again",
          verb: "QUERY",
          targets: ["terminal", "index", "civic records terminal"],
          label: "QUERY INDEX AGAIN",
          detail: "Re-run the municipal index comparison",
          requires: [{ type: "flag", value: "MUNICIPAL_RECORD_ANOMALY" }],
          consequence:
            "The terminal restates the official demolition date without admitting the physical hatch.",
        },
        {
          id: "return-from-records",
          verb: "RETURN",
          targets: ["core", "civic core", "counter"],
          label: "RETURN TO CIVIC CORE",
          detail: "Leave the sealed records pane",
          target: "civic-core",
        },
      ],
    },
    {
      id: "returned-civic-core",
      type: "HUB",
      x: 50,
      y: 38,
      state: "DISCOVERED",
      title: "CIVIC CORE // RETURNED",
      subtitle: "Changed municipal floor",
      body: [
        "The counter clerk no longer meets your eyes. The queue has thinned to two citizens who pretend not to watch the hatch.",
        "The public terminal now shows a sealed maintenance banner. The hatch hinge is damp with fresh sealant. The maintenance runner is gone.",
      ],
      present: ["counter clerk (averted)", "two waiting citizens"],
      objects: [
        "public terminal (sealed banner)",
        "service hatch (fresh sealant)",
        "notice board",
      ],
      actions: [
        {
          id: "look-returned",
          verb: "LOOK",
          targets: ["room", "core", "counter", "floor"],
          label: "LOOK AROUND",
          detail: "Read the changed civic floor",
          consequence:
            "Whatever you carried up from the subgrid has already altered the official room.",
        },
        {
          id: "inspect-sealed-hatch",
          verb: "INSPECT",
          targets: ["hatch", "service hatch", "sealant"],
          label: "INSPECT SEALED HATCH",
          detail: "Check the hatch after return",
          consequence:
            "The sealant is civic-issue. Someone wants the official surface restored.",
          effects: [{ type: "flag", value: "HATCH_SEAL_OBSERVED" }],
        },
        {
          id: "reenter-subgrid",
          verb: "MOVE",
          targets: ["subgrid", "service subgrid", "hatch"],
          label: "FORCE THE HATCH",
          detail: "Break the fresh seal and re-enter",
          requires: [{ type: "flag", value: "SERVICE_ROUTE_DISCOVERED" }],
          target: "service-subgrid",
        },
        {
          id: "report-returned",
          label: "FILE AN OFFICIAL REPORT",
          detail: "End the anomaly under municipal authority",
          target: "official-record",
        },
        {
          id: "conceal-from-return",
          label: "CONCEAL AND WITHDRAW",
          detail: "Leave with the illegal route unreported",
          requires: [{ type: "flag", value: "SUBGRID_TRAVERSED" }],
          target: "reclaimer-lie",
          effects: [{ type: "flag", value: "CONCEALED_RETURN" }],
        },
      ],
    },
    {
      id: "cable-pocket",
      type: "CONFLICT",
      x: 22,
      y: 70,
      state: "DANGER",
      title: "CABLE-RAT POCKET",
      subtitle: "Material pressure",
      body: [
        "A nest of wire-fed scavengers surrounds a dead municipal drone. Its memory wafer is still intact.",
      ],
      actions: [
        {
          id: "recover-wafer",
          label: "RECOVER MEMORY WAFER",
          detail: "Use BODY and the polytool to cross the nest",
          check: { attribute: "body", target: 8, salt: "cable-wafer" },
          successTarget: "service-subgrid",
          failureTarget: "service-subgrid",
          successEffects: [
            { type: "inventory", value: "memory-wafer" },
            { type: "flag", value: "WAFER_RECOVERED" },
          ],
          failureEffects: [
            { type: "resource", key: "integrity", delta: -2 },
            { type: "resource", key: "stress", delta: 1 },
          ],
        },
        {
          id: "withdraw-cable",
          label: "WITHDRAW",
          detail: "Return without the wafer",
          target: "service-subgrid",
        },
      ],
    },
    {
      id: "relic-seam",
      type: "TERMINAL",
      x: 78,
      y: 70,
      state: "INTRUSION",
      title: "RELIC SEAM // ACCESS",
      subtitle: "Intrusion pressure",
      body: [
        "The seal has no municipal manufacturer. Its geometry changes when DeckOS attempts to classify it.",
      ],
      actions: [
        {
          id: "jack-in",
          verb: "JACK",
          targets: ["seal", "node", "relic seam"],
          label: "JACK IN",
          detail: "Attempt a bounded systems breach",
          check: { attribute: "mind", target: 9, salt: "relic-seam" },
          successTarget: "signal-below",
          failureTarget: "signal-burn",
          successEffects: [
            { type: "flag", value: "RELIC_CONTACT" },
            { type: "resource", key: "signal", delta: 2 },
          ],
          failureEffects: [
            { type: "resource", key: "integrity", delta: -3 },
            { type: "resource", key: "stress", delta: 2 },
          ],
        },
        {
          id: "seal-with-cipher",
          label: "USE MAINTENANCE CIPHER",
          detail: "Open the civic bypass without direct contact",
          requires: [{ type: "inventory", value: "maintenance-cipher" }],
          target: "signal-below",
          effects: [{ type: "flag", value: "CIPHER_ENTRY" }],
        },
        {
          id: "withdraw-relic",
          label: "WITHDRAW",
          detail: "Return to the subgrid",
          target: "service-subgrid",
        },
      ],
    },
    {
      id: "signal-below",
      type: "PASSAGE",
      x: 64,
      y: 88,
      state: "UNKNOWN",
      title: "THE SIGNAL BELOW",
      subtitle: "Unauthorized continuation",
      body: [
        "The route does not open into another room. It opens into a remembered version of Sector 47 that never legally existed.",
      ],
      actions: [
        {
          id: "conceal-route",
          label: "CONCEAL THE ROUTE",
          detail: "Preserve the discovery outside official systems",
          target: "reclaimer-lie",
        },
        {
          id: "submit-evidence",
          label: "SUBMIT THE EVIDENCE",
          detail: "Return the anomaly to municipal authority",
          target: "official-record",
        },
      ],
    },
    {
      id: "signal-burn",
      type: "ENDING",
      x: 92,
      y: 88,
      state: "ENDING",
      title: "ENDING // SIGNAL BURN",
      subtitle: "Failure state",
      body: [
        "The seal answers by writing static through your sensory stack. DeckOS severs the link, but the illegal route is gone when you recover.",
      ],
      ending: "FAILURE",
      actions: [],
    },
    {
      id: "official-record",
      type: "ENDING",
      x: 36,
      y: 88,
      state: "ENDING",
      title: "ENDING // THE OFFICIAL RECORD",
      subtitle: "Municipal resolution",
      body: [
        "Your report is accepted, corrected, and sealed. By morning the service hatch has been replaced. The official map remains unchanged.",
      ],
      ending: "PARTIAL",
      actions: [],
    },
    {
      id: "reclaimer-lie",
      type: "ENDING",
      x: 8,
      y: 88,
      state: "ENDING",
      title: "ENDING // THE RECLAIMER'S LIE",
      subtitle: "Unauthorized continuity",
      body: [
        "You delete the report, preserve the route, and leave Civic Core carrying a second map beneath the legal one.",
      ],
      ending: "SUCCESS",
      actions: [],
    },
  ],
  routes: [
    ["boot", "civic-core"],
    ["civic-core", "civic-records-terminal"],
    ["civic-core", "mora-channel"],
    ["civic-core", "service-subgrid"],
    ["civic-core", "official-record"],
    ["civic-records-terminal", "civic-core"],
    ["service-subgrid", "cable-pocket"],
    ["service-subgrid", "relic-seam"],
    ["service-subgrid", "returned-civic-core"],
    ["returned-civic-core", "service-subgrid"],
    ["returned-civic-core", "official-record"],
    ["returned-civic-core", "reclaimer-lie"],
    ["relic-seam", "signal-below"],
    ["relic-seam", "signal-burn"],
    ["signal-below", "official-record"],
    ["signal-below", "reclaimer-lie"],
  ],
  loreEntries: {
    "municipal-erasure": {
      title: "Municipal Erasure",
      body: "Arcopolis civic systems can preserve a legal map that conflicts with surviving physical infrastructure.",
    },
    "civic-records": {
      title: "Civic Records",
      body: "A record can be administratively correct while materially false.",
    },
    "erased-service-routes": {
      title: "Erased Service Routes",
      body: "Maintenance indices retain ghost routes that public maps deny. DEVELOPMENT FIXTURE / NON-AUTHORITATIVE.",
    },
  },
};

export default sector47ModuleProof;
